import { Location, NavigateFunction, To } from 'react-router-dom';
import {
	Restrictions,
	IInputValue,
	checkValidation,
	IOfferPrice,
	IVignetteOffer,
	ITollSectionOffer,
	ProductType,
} from '../index';

const supplierIds = {
	oesterreich: 3,
	cze: 6,
	svn: 7,
	'one-cart': 0,
	schweiz: 14,
};

export const getUrlBasePath = (pathname: string, search?: URLSearchParams): To => {
	const isPreviewEnvironment = process.env.VERCEL_ENV !== 'production';
	const params = new URLSearchParams(search);
	if (isPreviewEnvironment) {
		params.set('featureAppPreview', 'true');
	}
	if (typeof window !== 'undefined') {
		const path = window.location.pathname;
		return {
			pathname: path.includes('fahrzeugwelt') ? `/maut-vignette/buchung${pathname}` : pathname,
			search: params.toString(),
		};
	}
	return {
		pathname,
		search: params.toString(),
	};
};
export const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

export const formatBytes = (bytes: number, decimals = 2) => {
	if (!Number(bytes)) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const formatPlates = (plates?: string | string[]) => {
	if (typeof plates === 'string') {
		return plates.trim().replace(/\s/g, '-');
	}

	return String(plates?.filter((element) => element !== '' && element !== undefined))
		.trim()
		.replace(/,/g, '-')
		.replace(/\s/g, '-');
};

export const formatPlatesPayload = (plates?: string | string[]) => {
	// Special case for when an Array is transformed to a string with the String() method
	if (typeof plates === 'string') {
		return plates.replace(/,/, '-').replace(/,/g, ' ');
	}

	if (plates?.length === 1) {
		return plates[0];
	}

	// Special format for German plates
	// e.g. M-UC-1234 is sent as M-UC 1234
	return String(plates?.filter((element) => element !== '' && element !== undefined))
		.replace(/,/, '-')
		.replace(/,/g, ' ');
};

export const convertPlatesToArray = (
	plates: string | string[],
	countryCode: string,
	isSpecialPlates: boolean,
): string[] => {
	const isArray = Array.isArray(plates);
	if (isArray) {
		return plates;
	}
	const formattedPlates = formatPlates(plates);
	if (!isArray && plates.includes('-')) {
		if (countryCode === 'DE' && !isSpecialPlates) {
			return formattedPlates.split('-');
		}
		return [formattedPlates];
	}
	return [formattedPlates];
};

export const removeAllWhitespaces = (value: string) => value.trim().replace(/ /g, '');

export const getRegexMatchedPattern = (regex: RegExp | undefined, value: string, arrayForm = false) => {
	if (!regex) {
		return undefined;
	}
	const matchedArray = regex.exec(value);
	if (arrayForm && matchedArray?.groups) return Object.values(matchedArray?.groups);
	return matchedArray ? matchedArray[0] : undefined;
};

export const getInputValues = (length: number, restrictions: Restrictions[]): IInputValue[] => {
	const inputValues = new Array(length).fill('');
	return inputValues.map((inputValue, index) => ({
		value: inputValue,
		restrictions: restrictions[index],
	}));
};

export const getInputFields = (option: string | null = null, isSpecialPlates?: boolean | undefined): IInputValue[] => {
	switch (option) {
		case 'DE':
			return isSpecialPlates
				? getInputValues(1, [{ inputRegex: undefined, maxLength: 10 }])
				: getInputValues(3, [
						{ inputRegex: new RegExp('[A-ZÖÜÄ]{1,3}'), maxLength: 3 },
						{ inputRegex: new RegExp('[A-ZÖÜÄ]{1,2}'), maxLength: 2 },
						{ inputRegex: new RegExp('[0-9]{1,4}[E,H]{0,1}'), maxLength: 5 },
				  ]);

		default:
			return getInputValues(1, [{ inputRegex: undefined, maxLength: 10 }]);
	}
};

export const getInitialValue = (documentCode, documentValue, country, dataType) => {
	if (!documentValue) return '';
	if (documentCode !== 'A') {
		if (dataType) return !checkValidation(documentValue, dataType.toUpperCase()) ? documentValue : '';
		return documentValue ?? '';
	}
	if (country) {
		const value = getInputFields(country.isoCode).map((i) => i.value);
		const matches = new RegExp(country.regexlicenseplate).exec(
			Array.isArray(documentValue) ? documentValue.join('') : removeAllWhitespaces(documentValue),
		);
		if (matches) {
			const matchedGroups = matches.slice(1);
			matchedGroups.forEach((groupValue = '', index) => (value[index] = groupValue.trim()));
			return value;
		}
		return [''];
	}
	return '';
};

export const setPaymentMethodsTitle = (
	paymentMethods: { id: number; name: string }[],
	labels?: (string | undefined)[],
) => {
	const singularLabel = labels ? labels[0] : 'Zahlungsart';
	const pluralLabel = labels ? labels[1] : 'Zahlungsart auswählen';
	return paymentMethods.length > 1 ? pluralLabel : singularLabel;
};

export const getFuelType = (selectedVignette: IVignetteOffer) =>
	selectedVignette?.fuelTypeId === -1 ? 'Benzin oder Diesel' : selectedVignette?.fuelType;

export const onRedirect = (redirectUrl: string) => {
	window.location.href = redirectUrl;
};

export const onOrderCompleted = (navigate: NavigateFunction) => {
	return (pathname: To) => navigate(pathname, { replace: true });
};

export const getSupplierIdByCountry = (location: Location): number => {
	const countryCode = Object.keys(supplierIds).find((key) => location.pathname.includes(key));
	const supplierId = supplierIds[countryCode ?? ''];
	return supplierId;
};

export const getFocusableElements = (element: React.MutableRefObject<HTMLElement | null> | HTMLElement) => {
	const isHtmlElement = element instanceof HTMLElement;
	const focusableElement = isHtmlElement ? element : element.current;
	const focusableElements = Array.from(
		focusableElement?.querySelectorAll(
			'a[href], button:not([aria-haspopup="menu"]), input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
		) ?? [],
	).filter((el) => !el.hasAttribute('disabled'));
	return {
		firstFocusableElement: focusableElements[0] as HTMLElement,
		lastFocusableElement: focusableElements[focusableElements.length - 1] as HTMLElement,
		focusableElements,
	};
};

type Ref = HTMLElement[] | HTMLElement;

export const getForwardedRef = (element: HTMLElement | null, ref: React.ForwardedRef<Ref>) => {
	if (!ref || !element || typeof ref === 'function') {
		return;
	}

	if (Array.isArray(ref.current)) {
		const foundIndex = ref.current.findIndex((c) => c.id === element.id);
		return foundIndex === -1 ? ref.current.push(element) : (ref.current[foundIndex] = element);
	}

	return (ref.current = element);
};

export const getFormattedTotalPrice = (prices?: IOfferPrice[]) =>
	`${prices?.find((price) => price.type === 'total')?.amount.amount}€`;

export const getFormattedArticlePrice = (prices?: IOfferPrice[]) =>
	`${prices?.find((price) => price.type === 'article')?.amount.amount}€`;

export const shouldCreateInvoice = (variant: string | null, additionalInfo: string | null) => {
	if (variant === 'dkv') {
		return true;
	}
	return additionalInfo ? JSON.parse(additionalInfo)?.createInvoice : false;
};

export const calculateCurrentStep = (
	produtTypeMap: Map<ProductType, boolean>,
	selectedSectionTolls: ITollSectionOffer[] | [],
	step: number,
	lastStep: number,
	isOneCartFlow = false,
	reuse = false,
) => {
	const currMap = new Map(produtTypeMap);
	const hasFlexService = currMap.get('flexProduct');
	const hasSectionToll = currMap.get('sectionToll');
	const hasAutVignette = currMap.get('autVignette');
	const hasSloVignette = currMap.get('sloVignette');
	const hasVignette = hasAutVignette || hasSloVignette;

	const vignetteAutStep = () => {
		const condition = isOneCartFlow ? step > 2 : step > 1;
		if ((hasAutVignette || hasSectionToll || hasFlexService) && condition) return 1;
		return 0;
	};

	const vignetteSloStep = () => {
		const calcStepCondition = () => {
			if (isOneCartFlow) {
				if (hasAutVignette || hasFlexService) return hasSectionToll ? 4 : 3;
				return 2;
			}
			return 1;
		};
		const condition = calcStepCondition();
		if (hasSloVignette && step > condition) return 1;
		return 0;
	};

	const sectionTollStep = () => {
		if (hasSectionToll) {
			return selectedSectionTolls.length && step > 3 ? 1 : 0;
		}

		return 0;
	};

	const vignetteDatesStep = () => {
		if (hasVignette && step > (reuse ? lastStep : lastStep - 1)) return 1;
		return 0;
	};

	return vignetteAutStep() + vignetteSloStep() + sectionTollStep() + vignetteDatesStep();
};

export const transformCloudinaryUrl = (url) => url.replace('res.cloudinary.com', 'assets.adac.de');

export const resetStorageAndContext = (
	flowId,
	deleteSelectedProducts,
	updateCart,
	updateTollSectionOffers?,
	updateIsTransitionPeriod?,
) => {
	const previousSupplierID = sessionStorage.getItem('flowId');
	if (previousSupplierID !== String(flowId)) {
		deleteSelectedProducts();
		sessionStorage.clear();
		updateCart();
		if (updateTollSectionOffers) updateTollSectionOffers([]);
		if (updateIsTransitionPeriod) updateIsTransitionPeriod(false);

		sessionStorage.setItem('flowId', String(flowId));
	}
};

type TurlDeepLinkParams = 'category' | 'countries' | 'vignettes' | 'section-tolls';

export const getUrlSearchParamsAsStack = (urlParamId: TurlDeepLinkParams, urlParams) => {
	const urlParamsById = urlParams.get(urlParamId);
	return urlParamsById ? urlParamsById.split(',').reverse() : [];
};

// Function to be called to initialize auto-scrolling functionalities
export const initDeepLinkAutoScroll = (urlParams) => {
	const urlPrefilledParams = [
		urlParams.get('category'),
		urlParams.get('countries'),
		urlParams.get('vignettes'),
		urlParams.get('section-tolls'),
	];
	const everyValueIsNull = urlPrefilledParams.every((params) => params === null);
	return !everyValueIsNull;
};
